import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Button } from 'Common/Supplyvue-ui'
import { PageLayout } from 'Common/Layout/PageLayout'
import { Panel } from 'Common/Components/Panel'
import { FormRow } from 'Common/Components/FormRow'
import { useState } from 'react'
import {
  useCreateProjectData,
  useSnapshotData,
  useSourceSites,
} from './CreateProject.data'
import { WorkcentreModal } from '../WorkcentreModal/WorkcentreModal.view'
import {
  SNAPSHOT_LENGTH_MENU_ITEMS,
  disableWeekdays,
  isValidDate,
  getSelectSnapshotDropdownMenuItems,
  getLastSunday,
} from './CreateProject.utils'
import { SourceSiteDetails } from '../WorkcentreModal/WorkcentreModal.types'
import { ProjectParamsInterface } from './CreateProject.types'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

export const projectParams = {
  projectName: '',
  snapshotName: '',
  selectedSnapshot: '',
  snapshotEndDate: getLastSunday(),
  snapshotLength: 0,
  workcentres: [],
}

export const CreateProjectPage = () => {
  const [projectParamsState, setProjectParamsState] =
    useState<ProjectParamsInterface>(projectParams)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isLoading: isSnapshotDataLoading, data: snapshotData } =
    useSnapshotData()

  const navigate = useNavigate()
  const { data: wcs, isLoading: isWcsLoading } = useSourceSites()

  const handleProjectNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      projectName: event.target.value,
    })
  }

  const handleSelectedSnapshotChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      selectedSnapshot: event.target.value as string,
    })
  }

  const handleSnapshotNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      snapshotName: event.target.value,
    })
  }

  const handleSnapshotLengthChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      snapshotLength: event.target.value as number,
    })
  }

  const handleEndDateChange = (date: Date | null) => {
    if (isValidDate(date) && date) {
      setProjectParamsState({
        ...projectParamsState,
        snapshotEndDate: date,
      })
    }
  }

  const handleOkbuttonClick = (selectedWorkcentres: SourceSiteDetails[]) => {
    setIsModalOpen(false)
    setProjectParamsState({
      ...projectParamsState,
      workcentres: selectedWorkcentres,
    })
  }

  const onCreateProjectSuccess = (projectId: string, message: string) => {
    toast.success(message)
    navigate('/')
  }
  const { isPending: isCreatingProject, mutate: createProject } =
    useCreateProjectData({ onCreateProjectSuccess })

  const handleCreateProject = () => {
    createProject(projectParamsState)
  }

  const isCreateProjectButtonDisabled = () => {
    if (projectParamsState.projectName === '') {
      return true
    }

    if (projectParamsState.selectedSnapshot === '') {
      return (
        projectParamsState.snapshotName === '' ||
        projectParamsState.snapshotLength === 0 ||
        projectParamsState.snapshotEndDate === null ||
        projectParamsState.workcentres.length === 0
      )
    }

    return false
  }

  const handleClearButtonClick = () => {
    setProjectParamsState({
      ...projectParams,
    })
    setIsModalOpen(false)
  }
  const importingNew = projectParamsState.selectedSnapshot === ''
  const snapshotFieldsDisabled = !importingNew || isSnapshotDataLoading
  const workcentreCount = projectParamsState.workcentres.length
  const createDisabled = isCreateProjectButtonDisabled()

  const createHint = !createDisabled
    ? ''
    : importingNew
      ? 'Name the project, then name, date and size the new snapshot and pick its work centres'
      : 'Name the project'

  return (
    <PageLayout
      title="Create project"
      actions={
        <>
          <Button variant="outlined" onClick={handleClearButtonClick}>
            Clear
          </Button>
          <Tooltip title={createHint}>
            <span>
              <Button
                onClick={handleCreateProject}
                disabled={createDisabled || isCreatingProject}
                variant="contained"
                startIcon={
                  isCreatingProject ? (
                    <CircularProgress color="inherit" size={14} />
                  ) : undefined
                }
              >
                Create project
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <Box
        sx={{
          maxWidth: 640,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Panel title="Project">
          <FormRow label="Project name" htmlFor="project-name">
            <TextField
              size="small"
              fullWidth
              id="project-name"
              value={projectParamsState.projectName}
              onChange={handleProjectNameChange}
              disabled={isSnapshotDataLoading}
            />
          </FormRow>
        </Panel>

        <Panel title="Data snapshot">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <FormRow label="Snapshot" htmlFor="select-snapshot">
              <TextField
                select
                size="small"
                fullWidth
                id="select-snapshot"
                value={projectParamsState.selectedSnapshot}
                onChange={(event) =>
                  handleSelectedSnapshotChange(
                    event as React.ChangeEvent<{ value: unknown }>
                  )
                }
                disabled={isSnapshotDataLoading}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    MenuProps: {
                      slotProps: { paper: { sx: { maxHeight: 300 } } },
                    },
                  },
                }}
              >
                <MenuItem value="">Import new snapshot</MenuItem>
                {(getSelectSnapshotDropdownMenuItems(snapshotData) ?? []).map(
                  (item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  )
                )}
              </TextField>
            </FormRow>

            {/* The rest describe a snapshot to import, so they only apply
                while Import new snapshot is chosen. */}
            <FormRow label="Snapshot name" htmlFor="snapshot-name">
              <TextField
                size="small"
                fullWidth
                id="snapshot-name"
                placeholder="Enter snapshot name"
                value={projectParamsState.snapshotName}
                onChange={handleSnapshotNameChange}
                disabled={snapshotFieldsDisabled}
              />
            </FormRow>
            <FormRow label="End date" htmlFor="snapshot-end-date">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  format="dd/MM/yyyy"
                  value={projectParamsState.snapshotEndDate}
                  maxDate={new Date()}
                  onChange={handleEndDateChange}
                  disabled={snapshotFieldsDisabled}
                  shouldDisableDate={disableWeekdays}
                  slotProps={{
                    textField: {
                      id: 'snapshot-end-date',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </FormRow>
            <FormRow label="Snapshot length" htmlFor="snapshot-length">
              <TextField
                select
                size="small"
                fullWidth
                id="snapshot-length"
                value={projectParamsState.snapshotLength || ''}
                onChange={(event) =>
                  handleSnapshotLengthChange(
                    event as React.ChangeEvent<{ value: unknown }>
                  )
                }
                disabled={snapshotFieldsDisabled}
              >
                {SNAPSHOT_LENGTH_MENU_ITEMS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormRow>
            <FormRow label="Work centres">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  onClick={() => {
                    setIsModalOpen(true)
                  }}
                  variant="outlined"
                  disabled={
                    projectParamsState.projectName === '' ||
                    !importingNew ||
                    isSnapshotDataLoading ||
                    isWcsLoading
                  }
                >
                  Select work centres
                </Button>
                <Box sx={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  {workcentreCount
                    ? `${workcentreCount} selected`
                    : 'None selected'}
                </Box>
              </Box>
            </FormRow>
          </Box>
        </Panel>
      </Box>
      <WorkcentreModal
        isUpdating={false}
        isModalOpen={isModalOpen}
        handleModalClose={() => {
          setIsModalOpen(false)
        }}
        sourceSiteData={wcs ?? []}
        handleOkbuttonClick={handleOkbuttonClick}
        readOnly={false}
      />
    </PageLayout>
  )
}
